import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Alert from './components/Alert';
import Login from './components/Login';
import HelpRequestsPanel from './components/HelpRequestsPanel';
import NotificationsPanel from './components/NotificationsPanel';
import UpdatesPanel from './components/UpdatesPanel';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [alert, setAlert] = useState(null);
  const [filters, setFilters] = useState({
    companyName: '',
    licenseType: 'All',
    status: 'All',
    expiresBefore: '',
    expiresAfter: ''
  });
  const [formData, setFormData] = useState({
    CompanyName: '',
    ContactEmail: '',
    LicenseType: 'Standard',
    IssueDate: new Date().toISOString().split('T')[0],
    ExpirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    IsActive: true
  });

  // Check for existing auth token on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setAuthToken(token);
      setIsAuthenticated(true);
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token) => {
    setAuthToken(token);
    setIsAuthenticated(true);
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('authToken');
    setAuthToken(null);
    setIsAuthenticated(false);
    setLicenses([]);
    setAlert(null); // Clear any existing alerts when logging out
  }, []);

  const fetchLicenses = useCallback(async () => {
    if (!authToken) return;
    
    try {
      setLoading(true);
      const response = await fetch('/api/licenses', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.status === 401 || response.status === 403) {
        showAlert('error', 'Session expired. Please login again.', handleLogout);
        return;
      }
      
      const data = await response.json();
      setLicenses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching licenses:', error);
      showAlert('error', 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchLicenses();
    }
  }, [isAuthenticated, authToken, fetchLicenses]);

  const showAlert = useCallback((type, message, onClose) => {
    setAlert({ type, message, onClose });
  }, []);

  const showConfirm = useCallback((message, onConfirm) => {
    setAlert({
      type: 'confirm',
      message,
      onConfirm: () => {
        setAlert(null);
        onConfirm();
      },
      onCancel: () => setAlert(null)
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const copyLicenseKey = () => {
    navigator.clipboard.writeText(editingLicense.LicenseKey).then(() => {
      showAlert('success', 'License key copied to clipboard');
    }).catch(() => {
      showAlert('error', 'Failed to copy license key');
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingLicense 
        ? `/api/licenses/${editingLicense.LicenseKey}`
        : '/api/licenses';
      
      const method = editingLicense ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(formData),
      });

      if (response.status === 401) {
        showAlert('error', 'Session expired. Please login again.', handleLogout);
        return;
      }

      if (response.ok) {
        showAlert('success', editingLicense ? 'License updated successfully' : 'License created successfully');
        setShowForm(false);
        setEditingLicense(null);
        resetForm();
        fetchLicenses();
      } else {
        const error = await response.json();
        showAlert('error', `Error: ${error.error || 'Failed to save license'}`);
      }
    } catch (error) {
      console.error('Error saving license:', error);
      showAlert('error', 'Failed to save license');
    }
  };

  const handleEdit = (license) => {
    setEditingLicense(license);
    setFormData({
      CompanyName: license.CompanyName || '',
      ContactEmail: license.ContactEmail || '',
      LicenseType: license.LicenseType || 'Standard',
      IssueDate: license.IssueDate ? license.IssueDate.split('T')[0] : '',
      ExpirationDate: license.ExpirationDate ? license.ExpirationDate.split('T')[0] : '',
      IsActive: license.IsActive
    });
    setShowForm(true);
  };

  const handleDeactivate = async (licenseKey, isActive) => {
    const action = isActive ? 'deactivate' : 'activate';
    
    showConfirm(`Are you sure you want to ${action} this license?`, async () => {
      try {
        const response = await fetch(`/api/licenses/${licenseKey}/${action}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (response.status === 401) {
          showAlert('error', 'Session expired. Please login again.', handleLogout);
          return;
        }

        if (response.ok) {
          showAlert('success', `License ${action}d successfully`);
          fetchLicenses();
        } else {
          showAlert('error', `Failed to ${action} license`);
        }
      } catch (error) {
        console.error(`Error ${action}ing license:`, error);
        showAlert('error', `Failed to ${action} license`);
      }
    });
  };

  const handleResetMachineId = async (licenseKey) => {
    showConfirm('Are you sure you want to reset the machine ID for this license? The license will be free to activate on a new machine.', async () => {
      try {
        const response = await fetch(`/api/licenses/${licenseKey}/reset-machine-id`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (response.status === 401) {
          showAlert('error', 'Session expired. Please login again.', handleLogout);
          return;
        }

        if (response.ok) {
          const updatedLicense = await response.json();
          showAlert('success', 'Machine ID reset successfully');
          setEditingLicense(updatedLicense);
          fetchLicenses();
        } else {
          showAlert('error', 'Failed to reset machine ID');
        }
      } catch (error) {
        console.error('Error resetting machine ID:', error);
        showAlert('error', 'Failed to reset machine ID');
      }
    });
  };

  const handleDelete = async (licenseKey) => {
    showConfirm('Are you sure you want to permanently delete this license?', async () => {
      try {
        const response = await fetch(`/api/licenses/${licenseKey}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (response.status === 401) {
          showAlert('error', 'Session expired. Please login again.', handleLogout);
          return;
        }

        if (response.ok) {
          showAlert('success', 'License deleted successfully');
          fetchLicenses();
        } else {
          showAlert('error', 'Failed to delete license');
        }
      } catch (error) {
        console.error('Error deleting license:', error);
        showAlert('error', 'Failed to delete license');
      }
    });
  };

  const resetForm = () => {
    setFormData({
      CompanyName: '',
      ContactEmail: '',
      LicenseType: 'Standard',
      IssueDate: new Date().toISOString().split('T')[0],
      ExpirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      IsActive: true
    });
  };

  const getLicenseStatus = (license) => {
    if (!license.IsActive) return 'Inactive';
    
    const now = new Date();
    const expiration = new Date(license.ExpirationDate);
    
    if (expiration < now) return 'Expired';
    
    const daysRemaining = Math.ceil((expiration - now) / (1000 * 60 * 60 * 24));
    if (daysRemaining <= 7) return `Expires in ${daysRemaining} day(s)`;
    
    return 'Active';
  };

  const getStatusClass = (license) => {
    const status = getLicenseStatus(license);
    if (status.includes('Expired') || status === 'Inactive') return 'status-expired';
    if (status.includes('Expires in')) return 'status-warning';
    return 'status-active';
  };

  const getExpirationClass = (license) => {
    const now = new Date();
    const expiration = new Date(license.ExpirationDate);
    const daysRemaining = Math.ceil((expiration - now) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining <= 7 && daysRemaining >= 0) {
      return 'expiration-warning';
    }
    if (daysRemaining < 0) {
      return 'expiration-expired';
    }
    return '';
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const clearFilters = () => {
    setFilters({
      companyName: '',
      licenseType: 'All',
      status: 'All',
      expiresBefore: '',
      expiresAfter: ''
    });
  };

  const getFilteredLicenses = () => {
    return licenses.filter(license => {
      // Company name filter
      if (filters.companyName && !license.CompanyName.toLowerCase().includes(filters.companyName.toLowerCase())) {
        return false;
      }

      // License type filter
      if (filters.licenseType !== 'All' && license.LicenseType !== filters.licenseType) {
        return false;
      }

      // Status filter
      if (filters.status !== 'All') {
        const status = getLicenseStatus(license);
        if (filters.status === 'Active') {
          // Active includes both fully active and expiring soon
          if (status === 'Inactive' || status === 'Expired') {
            return false;
          }
        } else if (filters.status === 'Expiring Soon') {
          // Only show licenses expiring within 7 days
          if (!status.includes('Expires in')) {
            return false;
          }
        } else if (filters.status === 'Inactive' && status !== 'Inactive') {
          return false;
        } else if (filters.status === 'Expired' && status !== 'Expired') {
          return false;
        }
      }

      // Expiration date filters
      const expirationDate = new Date(license.ExpirationDate);
      
      if (filters.expiresBefore) {
        const beforeDate = new Date(filters.expiresBefore);
        if (expirationDate > beforeDate) {
          return false;
        }
      }

      if (filters.expiresAfter) {
        const afterDate = new Date(filters.expiresAfter);
        if (expirationDate < afterDate) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredLicenses = getFilteredLicenses();

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div>
          <h1>TreeAuth</h1>
          <p>Manage licenses for TreeWorks</p>
        </div>
        <button className="btn btn-logout" onClick={handleLogout}>
          Logout
        </button>
      </header>

      <div className="container">
        <div className="section-header">
          <div>
            <h2>Licenses</h2>
            <p>Issue, edit, and revoke TreeWorks license keys.</p>
          </div>
          <div className="section-actions">
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditingLicense(null);
                resetForm();
                setShowForm(!showForm);
              }}
            >
              {showForm ? 'Cancel' : '+ New License'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={fetchLicenses}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="filter-bar">
          <div className="filter-group">
            <label>Search Company</label>
            <input
              type="text"
              name="companyName"
              placeholder="Company Name"
              value={filters.companyName}
              onChange={handleFilterChange}
            />
          </div>
          
          <div className="filter-group">
            <label>License Type</label>
            <select
              name="licenseType"
              value={filters.licenseType}
              onChange={handleFilterChange}
            >
              <option value="All">All Types</option>
              <option value="Standard">Standard</option>
              <option value="Enhanced">Enhanced</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Expiring Soon">Expiring Soon</option>
              <option value="Expired">Expired</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Expires After</label>
            <input
              type="date"
              name="expiresAfter"
              value={filters.expiresAfter}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label>Expires Before</label>
            <input
              type="date"
              name="expiresBefore"
              value={filters.expiresBefore}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary btn-small" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>

        {showForm && (
          <div className="form-container">
            <h2>{editingLicense ? 'Edit License' : 'Create New License'}</h2>
            {editingLicense && (
              <div className="license-key-display">
                <div className="license-key-wrapper">
                  <div>
                    <strong>License Key:</strong> {editingLicense.LicenseKey}
                  </div>
                  <button type="button" className="btn-copy-license" onClick={copyLicenseKey} title="Copy license key">
                    Copy
                  </button>
                </div>
                <div className="license-key-wrapper">
                  <div>
                    <strong>Machine ID:</strong> {editingLicense.MachineId || 'Not activated'}
                  </div>
                  {editingLicense.MachineId && (
                    <button
                      type="button"
                      className="btn-copy-license"
                      onClick={() => handleResetMachineId(editingLicense.LicenseKey)}
                      title="Reset machine ID"
                    >
                      Reset Machine ID
                    </button>
                  )}
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    name="CompanyName"
                    value={formData.CompanyName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Contact Email *</label>
                  <input
                    type="email"
                    name="ContactEmail"
                    value={formData.ContactEmail}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>License Type *</label>
                  <select
                    name="LicenseType"
                    value={formData.LicenseType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Standard">Standard</option>
                    <option value="Enhanced">Enhanced</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Issue Date *</label>
                  <input
                    type="date"
                    name="IssueDate"
                    value={formData.IssueDate}
                    readOnly
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Expiration Date *</label>
                  <input
                    type="date"
                    name="ExpirationDate"
                    value={formData.ExpirationDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="IsActive"
                    checked={formData.IsActive}
                    onChange={handleInputChange}
                  />
                  License is active
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingLicense ? 'Update License' : 'Create License'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditingLicense(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading licenses...</div>
        ) : licenses.length === 0 ? (
          <div className="empty-state">
            <p>No licenses found. Create your first license to get started.</p>
          </div>
        ) : filteredLicenses.length === 0 ? (
          <div className="empty-state">
            <p>No licenses match the current filters.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="licenses-table">
              <thead>
                <tr>
                  <th>License Key</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Machine ID</th>
                  <th>Expiration</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLicenses.map((license) => (
                  <tr key={license.LicenseKey}>
                    <td className="license-key" data-label="License Key">
                      <div className="license-key-cell">
                        {license.LicenseKey}
                        <button 
                          className="license-key-copy-btn" 
                          onClick={() => {
                            navigator.clipboard.writeText(license.LicenseKey);
                            showAlert('success', 'License key copied to clipboard');
                          }}
                          title="Copy license key"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td data-label="Company">{license.CompanyName}</td>
                    <td data-label="Email">{license.ContactEmail}</td>
                    <td data-label="Type">{license.LicenseType}</td>
                    <td className="machine-id" data-label="Machine ID">{license.MachineId || 'Not activated'}</td>
                    <td className={getExpirationClass(license)} data-label="Expiration">{new Date(license.ExpirationDate).toLocaleDateString()}</td>
                    <td data-label="Status">
                      <span className={`status-badge ${getStatusClass(license)}`}>
                        {getLicenseStatus(license)}
                      </span>
                    </td>
                    <td className="actions" data-label="Actions">
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(license)}
                        title="Edit license"
                      >
                        Edit
                      </button>
                      <button
                        className={`action-btn toggle-btn ${license.IsActive ? 'active' : 'inactive'}`}
                        onClick={() => handleDeactivate(license.LicenseKey, license.IsActive)}
                        title={license.IsActive ? 'Deactivate license' : 'Activate license'}
                      >
                        {license.IsActive ? 'Deactivate' : 'Activate'}
                      </button>
                      {!license.IsActive && (
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(license.LicenseKey)}
                          title="Delete license"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <HelpRequestsPanel
          authToken={authToken}
          onAuthExpired={handleLogout}
          showAlert={showAlert}
          showConfirm={showConfirm}
        />

        <NotificationsPanel
          authToken={authToken}
          onAuthExpired={handleLogout}
          showAlert={showAlert}
          showConfirm={showConfirm}
        />

        <UpdatesPanel
          authToken={authToken}
          onAuthExpired={handleLogout}
          showAlert={showAlert}
          showConfirm={showConfirm}
        />
      </div>

      {/* Custom Alert */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => {
            setAlert(null);
            if (alert.onClose) alert.onClose();
          }}
          onConfirm={alert.onConfirm}
          onCancel={alert.onCancel}
        />
      )}

      <footer className="App-footer">
        <p>&copy; {new Date().getFullYear()} The Kenerson Group. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;
